import { useSession } from "@/lib/auth/auth";
import { createFileRoute } from "@tanstack/react-router";
import debounce from "debounce";
import { uniq, uniqBy } from "lodash";
import { Ellipsis, Filter, Pencil, Search, SquareCheckBig } from "lucide-react";
import { PlusIcon } from "lucide-react";
import { useQueryState } from "nuqs";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Company } from "@/apis/gql/types";
import { AccountingFirmLayout } from "@/components/layouts/accounting-firm-layout";
import { NavUser } from "@/components/shared/app-navigation/navbar/nav-user";
import { MultiSelectStatusFilter } from "@/components/shared/data-table/filters/inputs/multi-select-filter";
import { CompanyOwnerForm } from "@/components/shared/form/userCompanies/owner";
import PageTitle from "@/components/shared/page-layout/PageTitle";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { LoadingSpinner } from "@/components/ui/spinner";
import { TypographyMuted, TypographySmall } from "@/components/ui/typography";
import { CreateSMBModal } from "@/features/company-setup/CreateSMBModal";
import { EditAccessModal } from "@/features/company-setup/EditAccessModal";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import { useGetAllCompanies } from "@/queries/companies/get-all";

export const Route = createFileRoute("/_authenticated/")({
  component: Index,
});

function Index() {
  const { t } = useTranslation();
  const { isSuperAdmin } = usePermissions();
  const { data: session } = useSession();
  const [search, setSearch] = useQueryState("q", { defaultValue: "" });
  const { isAccountingFirmAdmin } = usePermissions();
  const userId = session?.user?.id;

  const [filteredOwners, setFilteredOwners] = useState<string[]>([]);

  const { companies, loading } = useGetAllCompanies();

  const [showCreateModal, setShowCreateModal] = useState(false);

  const [accessModalCompany, setAccessModalCompany] = useState<Company | null>(
    null,
  );

  const debouncedSetSearch = useCallback(
    debounce((value: string) => setSearch(value), 200),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.clear();
    };
  }, [debouncedSetSearch]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <LoadingSpinner />
      </div>
    );
  }

  const filteredCompanies = companies.filter((company: Company) => {
    const searchTerm = search.toLowerCase().trim();

    const matchesSearch =
      !searchTerm ||
      company.companyName.toLowerCase().includes(searchTerm) ||
      (company.owner?.name?.toLowerCase().includes(searchTerm) ?? false);

    const matchesOwnerFilter =
      filteredOwners.length === 0 ||
      filteredOwners.includes(company.owner?.userId ?? "");

    return matchesSearch && matchesOwnerFilter;
  });

  const owners = uniqBy(
    [...companies, ...companies]
      .filter((company: Company) => company.owner)
      .map((company: Company) => ({
        value: company.owner?.userId ?? "",
        label: company.owner?.name ?? "",
      })),
    "value",
  );

  const handleMyClientsClick = () => {
    if (filteredOwners.includes(userId ?? "")) {
      setFilteredOwners(filteredOwners.filter((owner) => owner !== userId));
    } else {
      setFilteredOwners(uniq([...filteredOwners, userId ?? ""]));
    }
  };

  return (
    <AccountingFirmLayout>
      <div className="flex flex-col h-screen">
        <PageTitle location={[t("accounting-firm"), t("tenants")]}>
          <div className="flex flex-row gap-2 items-center">
            <NavUser />
          </div>
        </PageTitle>
        <div className="flex flex-row items-center gap-2 py-2 px-4 my-2">
          <div className="relative w-96">
            <Input
              defaultValue={search}
              placeholder={t("search_clients_placeholder")}
              onChange={(e) => debouncedSetSearch(e.target.value)}
              className="pl-7 w-full"
            />
            <Search
              width={16}
              height={16}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 select-none opacity-50"
            />
          </div>
          <MultiSelectStatusFilter
            label={t("accountant")}
            icon={Filter}
            options={owners}
            values={filteredOwners}
            onChange={setFilteredOwners}
          />
          <Button
            variant={
              filteredOwners.includes(userId ?? "") ? "default" : "outline"
            }
            onClick={handleMyClientsClick}
          >
            <SquareCheckBig className="mr-2 h-4 w-4" />
            {t("my_clients")}
          </Button>
          <div className="flex-1" />
          <Button onClick={() => setShowCreateModal(true)} className="mr-2">
            <PlusIcon className="mr-2 h-4 w-4 " />
            {t("createClient")}
          </Button>
        </div>
        <div className="flex flex-row gap-2 py-1 px-4 items-center">
          <div className="flex-1 px-2">
            <TypographyMuted>{t("tax_advisory_customer")}</TypographyMuted>
          </div>
          <div className="flex-1 px-2">
            <TypographyMuted>{t("accountant")}</TypographyMuted>
          </div>
          <div className="w-40 px-2 flex-shrink-0">
            <TypographyMuted>{t("actions")}</TypographyMuted>
          </div>
        </div>
        <div className="px-4 mt-1">
          <Separator />
        </div>
        <div className="flex-1 px-4 pb-4 overflow-y-auto relative">
          {filteredCompanies.length === 0 && (
            <Alert className="mt-4">
              <AlertTitle>{t("no_companies")}</AlertTitle>
              <AlertDescription>
                {t("no_companies_description")}
              </AlertDescription>
            </Alert>
          )}
          <ul className="grid grid-cols-1 gap-0">
            {filteredCompanies.map((company: Company) => {
              const isPending = company.status === "pending";
              const isNonSuperAdminPending = isPending && !isSuperAdmin;

              return (
                <li
                  key={company.companyId}
                  className="col-span-1 flex hover:bg-muted cursor-pointer"
                  onClick={() => {
                    if (!isNonSuperAdminPending) {
                      window.location.href = `/${company.companySlug}`;
                    }
                  }}
                >
                  <div className="w-full flex flex-row items-center">
                    <div className="flex-1">
                      {isNonSuperAdminPending ? (
                        <div className="flex flex-row items-center space-x-2 px-3 py-2">
                          <div className="flex-1">
                            <TypographySmall className="text-muted-foreground">
                              {company.companyName} (In Bearbeitung - Mandant
                              wird innerhalb von{" "}
                              {t("tenant_creation_timeframe")} erstellt)
                            </TypographySmall>
                          </div>
                        </div>
                      ) : (
                        <div
                          className={cn(
                            "flex flex-row items-center space-x-2 px-3 py-2",
                            isPending && "opacity-50",
                          )}
                        >
                          <div className="flex-1">
                            <TypographySmall
                              className={
                                isPending ? "text-muted-foreground" : ""
                              }
                            >
                              {company.companyName}
                            </TypographySmall>
                          </div>
                          {company.accountingFirmId && (
                            <div className="flex-1">
                              <div className="max-w-[250px] w-full">
                                <CompanyOwnerForm
                                  initialValues={{
                                    companyId: company.companyId,
                                    userId: company.owner?.userId ?? "",
                                  }}
                                  users={company.users ?? []}
                                  className="border-none hover:bg-muted"
                                />
                              </div>
                            </div>
                          )}
                          <div className="w-40 flex-shrink-0 flex justify-center items-center h-full">
                            <Button
                              variant="outline"
                              className="capitalize mr-3"
                            >
                              {t("open")}
                            </Button>
                            {isAccountingFirmAdmin && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="Aktionen"
                                    className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border bg-background hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 h-8 rounded-md gap-1.5 has-[>svg]:px-2.5 cursor-pointer shadow-none hover:shadow-none p-2 prevent-row-click"
                                    disabled={false}
                                  >
                                    <Ellipsis className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // needed to prevent everything from freezing; shadcn bug
                                      setTimeout(() => {
                                        setAccessModalCompany(company);
                                      }, 0);
                                    }}
                                  >
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Zugriffe bearbeiten
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <CreateSMBModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
      />
      {accessModalCompany && (
        <EditAccessModal
          open={!!accessModalCompany}
          onOpenChange={(open: boolean) => {
            if (!open) setAccessModalCompany(null);
          }}
          company={accessModalCompany}
        />
      )}
    </AccountingFirmLayout>
  );
}
